
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";
import {createStyledLabel} from "./DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "./ScreenSpaceScale.js";
import {createCopyIconMarker, wireCopyIconText, positionCopyIconAtLabelCorner} from "./CopyIconMarker.js";
import {setLabelWanted, setLabelLadder, setLabelRole, applyLabelOffset, LABEL_ROLE_RESULT} from "./labels/labelState.js";

import {t} from "./i18n/index.js";
const CYLINDER_COLOR = 0x2f8fd6;
// Persistent visual cue for a result accepted only via an explicit relaxed-
// tolerance retry (see tools/pipeTool.js / RelaxedFitPrompt.js) - matches the
// retry prompt's own warning-amber styling, so the two read as the same
// concept rather than introducing a third, unrelated color.
const REDUCED_ACCURACY_CYLINDER_COLOR = 0xd69a2f;
const AXIS_COLOR = 0xffdc00;
// Same numeric value as FeatureVisual.js's own COLOR_SELECTED - kept as an
// independent local constant rather than importing it, matching this file's
// existing convention of standalone colour constants (see
// REDUCED_ACCURACY_CYLINDER_COLOR's own comment on why a shared concept
// still gets its own local definition here).
const SELECTED_COLOR = 0xffa500;

function createAxisLine(){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({color: AXIS_COLOR, linewidth: 2, resolution: new THREE.Vector2(1000, 1000)});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

function setLinePositions(line, a, b){
	line.position.copy(a);
	line.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
	line.geometry.verticesNeedUpdate = true;
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
}

// Visual representation of a fitted Pipe feature (see tools/pipeTool.js and
// geometry/pipeAnalysis.js's computePipeResult()): a semi-transparent
// cylinder over the estimated pipe surface, its centerline, a small marker
// at the Measurement Station, and a result label (dimension + koter) with
// the standard copy-icon. Purely visual/interactive - registered data lives
// on the Feature as usual; DigitizationModule keeps this in sync.
//
// Unlike FeatureVisual, a pipe has no hand-placed, draggable vertices - its
// geometry is entirely computed (updateFromResult()), so there is no
// editing/insert-vertex support here.
export class PipeVisual extends THREE.Object3D {
	constructor(){
		super();

		this.name = "";
		this.description = "";
		this.result = null;
		// Same field name/semantics as FeatureVisual.labelsVisible (Digitalisering's
		// established label-visibility contract - see sidebar.js's Registreringer
		// label checkbox, which reads/drives this on every registered feature's
		// visual, whatever its concrete class). Real bug found via manual check:
		// PipeVisual previously had no setLabelsVisible() at all, so toggling a
		// Rør row's label checkbox threw (visual.setLabelsVisible is not a
		// function) - and since sidebar.js's group-toggle loop has no try/catch,
		// a single broken Pipe row would silently abort the label toggle for
		// every OTHER feature processed after it in the same group.
		this.labelsVisible = true;
		this.selected = false;

		// built at radius 1 / height 1, centered on the local origin, so
		// scale.x/z map directly to the fitted radius and scale.y to the
		// analyzed section length
		const geometry = new THREE.CylinderGeometry(1, 1, 1, 32, 1, true);
		const material = new THREE.MeshLambertMaterial({
			color: CYLINDER_COLOR,
			transparent: true,
			opacity: 0.35,
			depthWrite: false,
			side: THREE.DoubleSide,
		});
		this.cylinderMesh = new THREE.Mesh(geometry, material);
		this.cylinderMesh.visible = false;
		this.add(this.cylinderMesh);

		this.centerline = createAxisLine();
		this.add(this.centerline);

		this.stationMarker = new THREE.Mesh(
			new THREE.SphereGeometry(1, 16, 16), // kugle (var oktaeder/"pyramide")
			new THREE.MeshLambertMaterial({color: AXIS_COLOR, depthTest: false, depthWrite: false}),
		);
		this.stationMarker.visible = false;
		this.add(this.stationMarker);

		// "result" variant (task: "resultat-label skal have orange border") -
		// a Rør's one label IS its result label (dimension/koter summary,
		// task's own "Rør 3" example) - there is no separate per-vertex/
		// coordinate label class for a computed cylinder the way FeatureVisual
		// has one, so this is unconditionally the result-styled variant.
		this.label = createStyledLabel("", "result");
		setLabelRole(this.label, LABEL_ROLE_RESULT); // rørets ene label ER værktøjets samlede resultat (dimension, godstykkelse, alle koter)
		this.label.userData.pvIconPad = 26; // plads til kopi-ikonet i den enkeltlinjede (mindste) udgave, se labels/labelState.js
		this.isPipeVisual = true; // LabelManager (labels/) genkender rør på dette flag
		this.add(this.label);

		this._labelLines = null;

		this.copyIcon = createCopyIconMarker();
		// copies the rendered label's own text (dimension + koter), not the
		// station coordinate - a pipe registration's useful output is its
		// measurements, not a single XYZ point (see _refreshLabel())
		wireCopyIconText(this.copyIcon, () => this._labelLines ? this._labelLines.join("\n") : null);
		this.add(this.copyIcon);
	}

	setInfo(name, description){
		this.name = name || "";
		this.description = description || "";
		this._refreshLabel();
	}

	// Registreringer's label checkbox calls this on whatever visual a feature
	// has (see sidebar.js's toggleLabelCheckbox) - same method name/semantics
	// as FeatureVisual.setLabelsVisible: OFF hides labels only, never the
	// pipe geometry itself; ON restores the label if a result currently
	// exists to show. Goes through _refreshLabel() so this stays the single
	// place that decides "should the label actually be visible right now",
	// rather than duplicating that condition here.
	setLabelsVisible(visible){
		this.labelsVisible = visible;
		this._refreshLabel();
	}

	// Consolidation audit finding (task section 12, "uafhængig visibility"):
	// FeatureVisual has two genuinely independent flags (labelsVisible for
	// vertex/segment/summary labels, resultLabelVisible for its separate
	// result label) because it can genuinely show both at once. A Rør has
	// only ONE label total, and it IS the result label - so making these two
	// controls independent here would mean inventing a second flag that must
	// always be kept in lockstep with the first for no real behavioural
	// difference. Instead, both the blue and orange Registreringer toggles
	// drive this SAME underlying visibility for a Rör - honest given there is
	// only one thing to show or hide, not two parallel systems that happen
	// to agree.
	setResultLabelVisible(visible){
		this.setLabelsVisible(visible);
	}

	get resultLabelVisible(){
		return this.labelsVisible;
	}

	// Rørets ene label er resultatet: den almindelige label-genvej (L) hører ikke til et rør; kun resultat-genvejen (Shift+L) og Registreringers
	// knap styrer det. Læses af DigitizationModules L-handler.
	get labelsAreResultOnly(){
		return true;
	}

	// Always true once a fit has been computed (see updateFromResult()) -
	// unlike FeatureVisual's Punkt/Linje/Polygon, EVERY registered Rör has a
	// result label by definition (task: "Resultat-toggle skal kun være
	// relevant for objekter, der faktisk har et resultat-label").
	hasResultLabel(){
		return this.result !== null;
	}

	// Selection/highlight audit finding: PipeVisual had no setSelected() at
	// all, even though DigitizationModule.selectVisual() calls it
	// unconditionally on whatever visual is selected (whole-object hit via
	// _findHoveredWholeObject(), or the sidebar feature tree) - selecting a
	// Rør threw a TypeError. Recolours the same two meshes
	// _findHoveredWholeObject() already raycasts against
	// (cylinderMesh/stationMarker), same contract as FeatureVisual's own
	// setSelected(). The cylinder's own reduced-accuracy amber warning
	// (updateFromResult()) is preserved as the "unselected" base colour
	// rather than being clobbered - selecting a reduced-accuracy pipe still
	// shows it was fitted with a relaxed tolerance once deselected again.
	setSelected(value){
		this.selected = value;

		const baseCylinderColor = (this.result && this.result.reducedAccuracy) ? REDUCED_ACCURACY_CYLINDER_COLOR : CYLINDER_COLOR;
		this.cylinderMesh.material.color.setHex(value ? SELECTED_COLOR : baseCylinderColor);
		this.stationMarker.material.color.setHex(value ? SELECTED_COLOR : AXIS_COLOR);
	}

	// positions/scales the cylinder, centerline and station marker from a
	// computePipeResult() result object, and rebuilds the result label
	updateFromResult(result){
		this.result = result;

		const {axisDir, radius, sectionLength, station} = result;
		const length = Math.max(sectionLength, radius * 2.2);

		const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axisDir);
		this.cylinderMesh.quaternion.copy(quaternion);
		this.cylinderMesh.position.copy(station);
		this.cylinderMesh.scale.set(radius, length, radius);
		this.cylinderMesh.visible = true;
		this.cylinderMesh.material.color.setHex(result.reducedAccuracy ? REDUCED_ACCURACY_CYLINDER_COLOR : CYLINDER_COLOR);

		const half = axisDir.clone().multiplyScalar(length / 2);
		setLinePositions(this.centerline, station.clone().sub(half), station.clone().add(half));
		this.centerline.visible = true;

		this.stationMarker.position.copy(station);
		this.stationMarker.visible = true;

		this._refreshLabel();
	}

	_refreshLabel(){
		if(!this.result){
			setLabelWanted(this.label, false);
			this._labelLines = null;
			return;
		}

		const r = this.result;
		const dimensionMm = (r.radius * 2 * 1000).toFixed(1);

		const lines = [this.name || t("Rørmåling")];
		if(r.reducedAccuracy) lines.push(t("⚠ Reduceret nøjagtighed (løsere tolerance)"));
		lines.push(`Dimension: ${dimensionMm} mm`);

		if(r.wallThicknessMeters != null){
			lines.push(t("Godstykkelse: {0} mm", Math.round(r.wallThicknessMeters * 1000)));
		}

		lines.push(t("Ydre topkote: {0} m", r.outerTopZ.toFixed(3)));
		if(r.innerTopZ != null) lines.push(t("Indre topkote: {0} m", r.innerTopZ.toFixed(3)));
		lines.push(t("Centerkote: {0} m", r.centerZ.toFixed(3)));
		if(r.innerBottomZ != null) lines.push(t("Indre bundkote: {0} m", r.innerBottomZ.toFixed(3)));
		lines.push(t("Ydre bundkote: {0} m", r.outerBottomZ.toFixed(3)));

		this._labelLines = lines;
		// Rørmålingens label ER værktøjets resultat (dimension, godstykkelse, koter): ét sammenhængende indhold, aldrig reduceret.
		setLabelLadder(this.label, [null, null, null, lines.join(String.fromCharCode(10))]);
		this.label.position.copy(r.station);
		setLabelWanted(this.label, this.labelsVisible);
	}

	updateResolution(width, height){
		this.centerline.material.resolution.set(width, height);
	}

	// axes: {right, up, forward} fra LabelManager - lægger labelens skærm-forskydning oven på ankeret
	updateScale(camera, clientWidth, clientHeight, axes){
		const cameraPos = camera.position;

		if(this.stationMarker.visible){
			const s = computeScreenSpaceScale(
				camera, this.stationMarker.getWorldPosition(new THREE.Vector3()), cameraPos,
				clientWidth, clientHeight, ScreenSpaceSizes.computedPoint);
			this.stationMarker.scale.set(s, s, s);
		}

		if(this.label.visible){
			const s = computeScreenSpaceScale(
				camera, this.label.getWorldPosition(new THREE.Vector3()), cameraPos,
				clientWidth, clientHeight, ScreenSpaceSizes.label);
			this.label.scale.set(s, s, s);
			if(axes) applyLabelOffset(this.label, axes, camera);
		}

		positionCopyIconAtLabelCorner(this.copyIcon, this.label, camera, clientWidth, clientHeight);
	}

	// GPU-resource teardown for when this Pipe feature is deleted outright
	// (see DigitizationModule.js's removeFeature()) - every object here is
	// created exactly once in the constructor and kept for this visual's
	// whole lifetime (updateFromResult() only repositions/rescales them), so
	// this is the only place any of it needs releasing.
	dispose(){
		this.cylinderMesh.geometry.dispose();
		this.cylinderMesh.material.dispose();
		this.centerline.geometry.dispose();
		this.centerline.material.dispose();
		this.stationMarker.geometry.dispose();
		this.stationMarker.material.dispose();
		this.label.dispose();
		this.label.userData.pvRec = null;
		this.copyIcon.geometry.dispose();
		this.copyIcon.material.map.dispose();
		this.copyIcon.material.dispose();
	}
};
